import React, { useState, useEffect } from 'react'
import login from '../../services/auth.service'
import { useForm } from "react-hook-form"
import { useEyeconxStore } from "../../store/eyeconxStore"
import { useNavigate, Link } from "react-router-dom"
import { toAbsoluteUrl } from '../helpers/AssetHelpers'
function Login() {
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm();

    const [message, setMessage] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const setToken = useEyeconxStore((state) => state.setToken);
    const setUser = useEyeconxStore((state) => state.setUser);
    const setIsAuthenticate = useEyeconxStore((state) => state.setIsAuthenticate);
    const logout = useEyeconxStore(state => state.logout);
    const user = useEyeconxStore((state) => state.user);
    const token = useEyeconxStore((state) => state.token);
    const isAuthenticated = useEyeconxStore((state) => state.isAuthenticated);

    const navigate = useNavigate();

    // Load saved credentials
    useEffect(() => {
        const savedCredentials = localStorage.getItem('rememberedCredentials');
        if (savedCredentials) {
            const { username, password } = JSON.parse(savedCredentials);
            setValue('username', username);
            setValue('password', password);
            setRememberMe(true);
        }
    }, [setValue]);

    const onSubmit = async (data) => {
        setMessage(null);
        try {
            await login(data.username, data.password).then((res) => {
                if (res.data.statusCode === 200) {
                    const user = {
                        ID: res.data.uId,
                        name: res.data.username,
                        role: res.data.role,
                        clinicId: res.data.clinicId,
                        authorization: 'Bearer ' + res.data.token,
                        clinicType: res.data.clinicType,
                    };

                    setToken(res.data.token);
                    setUser(user);
                    setIsAuthenticate(true);

                    if (rememberMe) {
                        localStorage.setItem('rememberedCredentials', JSON.stringify({
                            username: data.username,
                            password: data.password
                        }));
                    } else {
                        localStorage.removeItem('rememberedCredentials');
                    }

                    localStorage.removeItem('notifications');
                }
            });
        } catch (error) {
            console.error(error);
            if (error?.response) {
                setMessage(error.response.data.error || "Login failed. Please try again.");
            } else {
                setMessage("Network error. Please check your connection.");
            }
        }
    };

    useEffect(() => {
        if (isAuthenticated && token != null) {
            if (user.clinicType === "EYECONX") {
                navigate("/dashboard")
            } else if (user.clinicType === "SMS_GATEWAY") {
                navigate("/sms-dashboard")
            } else {
                logout()
                navigate('/login')
            }
        }
    }, [user])

    return (
        <div className="h-100 d-flex align-items-center justify-content-center py-5 min-vh-90">
           
                <div className="card shadow-lg border-0 rounded-2 w-100 w-lg-500">
                    <div className="card-body p-10 p-lg-15 p-md-5">

                        {/* Logo / Icon */}
                        <div className="text-center mb-10">
                             <img 
                                                                    src={toAbsoluteUrl('media/logos/eyeconx-logo.png')} 
                                                                    alt="Eyeconx Logo" 
                                                                    className="h-50px mb-5"
                                                                    height={"50px"}
                                                                />
                            <h1 className=" fw-bolder mb-3 text-dark mt-2">Sign In</h1>
                        </div>

                        {/* Error Message */}
                        {message && (
                            <div className="alert alert-danger d-flex align-items-center p-3 mb-4 rounded-3">
                                <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                                <div className="flex-grow-1">{message}</div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setMessage(null)}
                                    aria-label="Close"
                                ></button>
                            </div>
                        )}

                        {/* Login Form */}
                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            {/* Username */}
                            <div className="mb-3 w-100">
                                <label className="form-label fw-semibold text-dark fs-6">Username</label>
                                <div className="input-group input-group-md ">
                                    <span className="input-group-text bg-light">
                                        <i className="bi bi-person-fill text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className={`form-control form-control-lg form-control-solid ${errors.username ? 'is-invalid' : ''}`}
                                        placeholder="Enter your username"
                                        {...register("username", { required: "Username is required" })}
                                    />
                                    {errors.username && (
                                        <div className="invalid-feedback">{errors.username.message}</div>
                                    )}
                                </div>
                            </div>

                            {/* Password */}
                            <div className="mb-4">
                                <label className="form-label fw-semibold fs-6">Password</label>
                                <div className="input-group input-group-md">
                                    <span className="input-group-text bg-light">
                                        <i className="bi bi-lock-fill text-muted"></i>
                                    </span>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        className={`form-control form-control-lg form-control-solid ${errors.password ? 'is-invalid' : ''}`}
                                        placeholder="Enter your password"
                                        {...register("password", { required: "Password is required" })}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-light text-muted"
                                        onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                                    </button>
                                    {errors.password && (
                                        <div className="invalid-feedback">{errors.password.message}</div>
                                    )}
                                </div>
                            </div>

                            {/* Remember + Forgot */}
                            <div className="d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold justify-content-between align-items-center mb-4">
                                <div className="form-check">
                                    <input
                                        className="form-check-input fs-6"
                                        type="checkbox"
                                        id="rememberMe"
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="rememberMe">
                                        Remember me
                                    </label>
                                </div>
                                <Link to="/forgot-password" className="text-decoration-none small fw-semibold text-primary">
                                    Forgot Password?
                                </Link>
                            </div>

                            {/* Submit */}
                            <div className="d-grid">
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-md fw-semibold text-white"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Signing in...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-box-arrow-in-right me-2"></i>
                                            Login
                                        </>
                                    )}
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
      
    )
}

export default Login
